import { useState } from "react";
import { useAdminApi } from "../pages/admin/adminUtils";
import { resolveImg } from "./calendarUtils";
import "./ImageField.css";

const MAX_EDGE = 1200;

// Downscale large photos in the browser before upload (keeps files well under the 5 MB limit).
async function shrink(file) {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type) || typeof createImageBitmap !== "function") return file;
  const bmp   = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bmp.width, bmp.height));
  if (scale === 1 && file.size < 1.5e6) return file;
  const canvas  = document.createElement("canvas");
  canvas.width  = Math.round(bmp.width * scale);
  canvas.height = Math.round(bmp.height * scale);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise(res => canvas.toBlob(res, "image/jpeg", 0.85));
  return blob ? new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }) : file;
}

export default function ImageField({ value, onChange, label = "Image" }) {
  const { API, TOKEN } = useAdminApi();
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState("");

  const onFile = async e => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("image", await shrink(file));
      const res  = await fetch(`${API}/uploads/images`, { method: "POST", headers: { Authorization: `Bearer ${TOKEN}` }, body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.path) throw new Error(data.error || "Upload failed.");
      onChange(data.path);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const src = resolveImg(API, value);
  return (
    <div className="image-field">
      <span className="image-field-label">{label}</span>
      <div className="image-field-row">
        {src
          ? <img className="image-field-thumb" src={src} alt="" />
          : <div className="image-field-thumb image-field-thumb--empty">No image</div>}
        <div className="image-field-actions">
          <label className={`image-field-btn ${busy ? "is-busy" : ""}`}>
            {busy ? "Uploading…" : value ? "Replace" : "Upload"}
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={onFile} disabled={busy} hidden />
          </label>
          {value && !busy && <button type="button" className="image-field-remove" onClick={() => onChange("")}>Remove</button>}
        </div>
      </div>
      {error && <p className="image-field-error">{error}</p>}
    </div>
  );
}
